import React, { useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ReactEcharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { ScatterChart as ScatterChartType } from '../../../../types/charts';
import { useEChartsTheme, getChartColors, useTheme } from '../../../../theme';
import { chartImageExtractor } from '../../../../helpers/chartImageExtractor';

interface ScatterChartProps {
  chartData: ScatterChartType;
  width?: number;
  height?: number;
  chartId?: string;
}

const ScatterChart: React.FC<ScatterChartProps> = ({
  chartData,
  width = 400,
  height = 300,
  chartId
}) => {
  const theme = useTheme();
  const echartsTheme = useEChartsTheme();
  const chartRef = useRef<any>(null);

  const option: EChartsOption = useMemo(() => {
    const { data, config, title } = chartData;
    const defaultColors = getChartColors(theme, 'default');

    return {
      title: title
        ? {
            text: title,
            left: 'left',
            textStyle: {
              fontSize: 16,
              fontWeight: 500,
              color: echartsTheme.textColor
            }
          }
        : undefined,
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const { data } = params;
          return `${data.name}<br/>X: ${data.value[0]}<br/>Y: ${data.value[1]}`;
        },
        backgroundColor: echartsTheme.tooltipBg,
        borderColor: echartsTheme.borderColor,
        borderWidth: 1,
        textStyle: {
          color: echartsTheme.textColor
        },
        confine: true,
        z: 9999
      },
      grid: {
        left: '10%',
        right: '4%',
        bottom: '10%',
        top: title ? '15%' : '10%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: config?.xAxisLabel || '',
        nameLocation: 'middle',
        nameGap: 30,
        axisLine: {
          lineStyle: {
            color: echartsTheme.borderColor
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: echartsTheme.textColor
        },
        splitLine: {
          lineStyle: {
            color: echartsTheme.splitLineColor
          }
        }
      },
      yAxis: {
        type: 'value',
        name: config?.yAxisLabel || '',
        nameLocation: 'middle',
        nameGap: 50,
        axisLine: {
          lineStyle: {
            color: echartsTheme.borderColor
          }
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: echartsTheme.textColor
        },
        splitLine: {
          lineStyle: {
            color: echartsTheme.splitLineColor
          }
        }
      },
      series: [
        {
          type: 'scatter',
          data: data.map((item, index) => ({
            value: [item.x, item.y],
            name: item.name,
            itemStyle: {
              color:
                item.color || config?.colors?.[index] || defaultColors[index % defaultColors.length]
            }
          })),
          symbolSize: config?.symbolSize || 8,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            }
          }
        }
      ]
    };
  }, [chartData, theme, echartsTheme]);

  // Register chart instance for image extraction
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (chartId) {
        chartImageExtractor.unregisterChart(chartId);
      }
    };
  }, [chartId]);

  const onChartReady = (chart: any) => {
    if (chartId) {
      chartImageExtractor.registerChart(chartId, chart);
    }
  };

  return (
    <ChartContainer>
      <ReactEcharts
        ref={chartRef}
        option={option}
        style={{ width: `${width}px`, height: `${height}px` }}
        opts={{ renderer: 'canvas' }}
        onChartReady={onChartReady}
      />
    </ChartContainer>
  );
};

export default ScatterChart;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;
